
import { Component, Prop, PropSync, Vue, Watch } from 'vue-property-decorator';
import { StepDocumentFlat } from '@/models/testcase.model';
import SbFlowOneStep from '@/components/stead/workspaces/stepbuilder/sbFlowOneStep.vue';
import { TObjectDocument } from '@/models/tobject.model';
import SbInsertionDialog from '@/components/stead/workspaces/stepbuilder/dialogboxes/sbInsertionDialog.vue';
import draggable from 'vuedraggable';
import { IInsertRush } from "@/models/buffer.model";
import { ajustHeight } from '@/shared/dom.utils';
import { StepLightDocument } from "@/models/step.model";

@Component({
  components: {SbInsertionDialog, SbFlowOneStep, draggable}
})
export default class SbFlow extends Vue {
  // Les props
  @Prop() private tcid!: string
  @Prop({default: false}) private loading!: boolean
  @Prop({default: false}) private connected!: boolean
  @PropSync('cursor', {default: -1}) private stepCursor!: number
  @PropSync('cursor_step', {default: ''}) private cursorStep!: StepLightDocument | null
  @PropSync('selection') private rangeSelection!: number[]

  // Les propriétés
  private showDialogInsertion: boolean = false
  private height: number = 0
  private virtualScrollDiv: HTMLElement | null = null
  private readonly itemHeight: number = 32
  private insertPos: number = -1

  // Les propriétés calculées
  private get toolbarColor() {
    return this.$store.state.testcase.theme.tree.doc1
  }

  private get steps(): StepLightDocument[] {
    const result = [...this.$store.state.step.steps]
    result.push(null as unknown as any)
    return result
  }

  private get loadingScreens() {
    return this.$store.state.step.shotloading
  }

  // Les hooks
  public mounted() {
    this.virtualScrollDiv = document.getElementById('sbflow')
    if (this.virtualScrollDiv) {
      this.virtualScrollDiv.onkeydown = this.onKeydown
    }
    window.addEventListener('resize', this.ajustHeight)
    this.$nextTick(this.ajustHeight)
  }

  public beforeDestroy() {
    window.removeEventListener('resize', this.ajustHeight)
  }

  // Les méthodes surveillées
  @Watch('stepCursor')
  private onChangeStepCursor() {
    // Maintient l'étape sélectionnée visible lors des actions clavier flèche-haut/flèche-bas
    if (this.virtualScrollDiv) {
      const scrollTop = this.virtualScrollDiv.scrollTop
      const ph = scrollTop / this.itemHeight
      const pb = (scrollTop + this.height) / this.itemHeight
      const posHaut = Math.round(ph)   // N° de la 1ère étape visible en haut de la fenêtre de visualisation
      const posBas = Math.round(pb) - 1 // N° de la dernière étape visible en bas de la fenêtre de visualisation
      // console.log(`scrollTop:${scrollTop}, posHaut:${posHaut}(${ph}), posBas:${posBas}(${pb}), nbItems:${this.height/this.itemHeight}`)
      if (this.stepCursor < posHaut || this.stepCursor > posBas) {
        const move = this.stepCursor - (this.stepCursor > posBas ? posBas : posHaut)
        this.virtualScrollDiv.scrollTop = (posHaut + move) * this.itemHeight
      }
      this.virtualScrollDiv.focus()
    }
  }

  @Watch('steps')
  private onChangeSteps() {
    if (this.stepCursor > this.steps.length) {
      this.stepCursor = this.steps.length
    }
  }

  // Les méthodes d'instance
  private ajustHeight() {
    this.height = ajustHeight('sbflow', 64) - (this.virtualScrollDiv ? this.virtualScrollDiv.scrollTop : 0)
  }

  private onInsert(index: number) {
    this.insertPos = index
    if(this.stepCursor === -1) {
      this.stepCursor = 0
    }
    this.showDialogInsertion = true
  }

  private onSelectedTObject(tobj: TObjectDocument) {
    this.$emit('insert-step', {pos: this.insertPos, tobj: {...tobj}})
  }

  private async onDelete(step: StepLightDocument) {
    this.stepCursor = -1 // Warning ! This instruction must be placed before "await" instruction
    this.cursorStep = null
    await this.$store.dispatch('step/removeStep', {
      tcid: this.tcid,
      stepid: step._id
    })
  }

  private onKeydown(ev: KeyboardEvent) {
    // console.log('onKeydown ', ev.key)
    ev.preventDefault()
    switch (ev.key) {
      case 'ArrowUp' :
        this.stepCursorUp()
        this.inSelection(ev.shiftKey)
        break
      case 'ArrowDown' :
        this.stepCursorDown()
        this.inSelection(ev.shiftKey)
        break
      case 'Escape' :
        this.rangeSelection = []
        break
      case 'c':
      case 'r':
      case 'x':
      case 'v':
      case 'a':
        if (ev.ctrlKey && !this.connected) {
          const keyevent = new Map([['c', 'copy'], ['r', 'rush'], ['x', 'cut'], ['a', 'selectall'], ['v', 'paste']])
          const arg = keyevent.get(ev.key)
          if (arg) {
            this.$emit('eventkey', arg)
          }
        }
        break
      case 'F5':
        this.$emit('evenkey', 'run')
        break
      case 'F6':
        this.$emit('eventkey', 'debug')
        break
      case 'F8':
        this.$emit('eventkey', 'dbgnext')
        break
      case 'F9':
        this.$emit('eventkey', 'dbgresume')
        break
      case 'F10':
        this.$emit('eventkey', 'dbgstop')
        break
    }
  }

  private onClickStep(pos: number, step: StepLightDocument | null) {
    this.stepCursor = pos
    this.cursorStep = step
  }

  private stepCursorUp() {
    let cursor: number = 0
    if (this.stepCursor > 0) {
      cursor = this.stepCursor - 1
    }
    // Il faut obligatoirement respecter l'ordre d'affectation des propriétés synchronisées cursorStepId et stepCursor
    // sinon il se produit un décalage et le cursorStepId ne correspond plus au step dont la position est stepCursor.
    // D'où la nécessité de passer par une variable intermédiaire.
    this.cursorStep = this.steps[cursor]
    this.stepCursor = cursor

  }

  private stepCursorDown() {
    if (this.stepCursor < this.steps.length - 2) {
      // Idem ci-dessus
      const cursor = this.stepCursor + 1
      this.cursorStep = this.steps[cursor]
      this.stepCursor = cursor
    } else {
      this.stepCursor = this.steps.length - 1
      this.cursorStep = null
    }
  }

  private isSelected(pos: number): boolean {
    const idx = this.rangeSelection.findIndex((i) => i === pos)
    return idx >= 0
  }

  private inSelection(flag: boolean) {
    if (flag && !this.isSelected(this.stepCursor)) {
      this.rangeSelection = [...this.rangeSelection, this.stepCursor]
    }
  }

  private onInsertRush(stepsWithId: StepDocumentFlat[]) {
    const data: IInsertRush = {
      pos: this.insertPos,
      steps: stepsWithId.map(s => {
        return {
          tobject: s.tobject,
          instruc: s.instruc,
          parameters: s.parameters
        }
      })
    }
    this.$emit('rush', data)
  }

  // Les méthodes statiques
}
